// ActionTemplatesView — admin UI for Process Templates.
//
// Three states:
//   list    → grid of templates (system + agency-built) with category chips
//   editor  → create or edit a custom template (name, tasks, days offsets)
//   detail  → template detail with task timeline + apply-stats
//
// Also exports <ApplyTemplateModal> used from ClientDetail's "Apply Template" button.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import DateField from './DateField.jsx';
import {
  Plus, Edit2, Trash2, Copy, X, Check, ChevronDown, AlertCircle,
  Sparkles, Clock, Calendar, FileText, Loader2, ArrowRight,
} from "lucide-react";
import {
  listTemplates, createTemplate, updateTemplate, deleteTemplate,
  getTemplate, applyTemplate,
} from "../api.js";
import { confirmDialog } from "./ui.jsx";

const CATEGORY_META = {
  ONBOARDING: { label: "Onboarding", color: "#10b981" },
  RENEWAL:    { label: "Renewal",    color: "#3b82f6" },
  AEP:        { label: "AEP",        color: "#a855f7" },
  CLAIM:      { label: "Claim",      color: "#ef4444" },
  CROSS_SELL: { label: "Cross-sell", color: "#f59e0b" },
  COMPLIANCE: { label: "Compliance", color: "#06b6d4" },
  GENERAL:    { label: "General",    color: "#6b7280" },
};

const PRIORITY_META = {
  LOW:    { label: "Low",    color: "#6b7280" },
  MEDIUM: { label: "Medium", color: "#3b82f6" },
  HIGH:   { label: "High",   color: "#f59e0b" },
  URGENT: { label: "Urgent", color: "#ef4444" },
};

// ----------------------------------------------------------------------
// Spanish translations for the 5 SYSTEM templates seeded by the backend.
// The keys match the English `name` from seed-process-templates.ts so we
// can map at render time without touching the DB. Custom (non-system)
// templates created by an agency are shown verbatim in whatever language
// the agency typed them.
// ----------------------------------------------------------------------

const SYSTEM_TEMPLATE_ES = {
  'Onboarding Health Client': {
    name: 'Onboarding Cliente de Salud',
    description: 'Flujo completo de onboarding para un nuevo cliente de seguro de salud — desde elegibilidad hasta el paquete de bienvenida.',
    tasks: {
      'Verify client eligibility':            'Verificar elegibilidad del cliente',
      'Request SSN + ID documents':           'Solicitar SSN + documentos de identidad',
      'Generate quote with 3 plan options':   'Generar cotización con 3 opciones de plan',
      'Send proposal + schedule review call': 'Enviar propuesta + agendar llamada de revisión',
      'Follow up on proposal (day 3)':        'Dar seguimiento a la propuesta (día 3)',
      'Process enrollment':                   'Procesar inscripción',
      'Confirm policy issued + delivery':     'Confirmar emisión + entrega de la póliza',
      'Send welcome packet':                  'Enviar paquete de bienvenida',
    },
    taskDescriptions: {
      'Verify client eligibility':            'Confirmar ingresos, tamaño del hogar, ciudadanía, ZIP para elegibilidad del marketplace.',
      'Request SSN + ID documents':           'Enviar una Solicitud de Documentos de Deskte para: tarjeta de SSN, ID con foto, comprobante de domicilio, comprobante de ingresos.',
      'Generate quote with 3 plan options':   'Construir una Cotización con comparación Bronze, Silver y Gold de las aseguradoras correspondientes.',
      'Send proposal + schedule review call': 'Enviar el link público de la cotización + agendar llamada de 30 min.',
      'Follow up on proposal (day 3)':        'Si aún no acepta, llamar o enviar mensaje para responder dudas.',
      'Process enrollment':                   'Enviar la aplicación a la aseguradora una vez aceptado el plan.',
      'Confirm policy issued + delivery':     'Verificar número de póliza, prima y fecha efectiva. Enviar tarjetas de ID.',
      'Send welcome packet':                  'Email de bienvenida con resumen de beneficios, tarjetas de ID, contactos para reclamos, tu línea directa.',
    },
  },
  'Renewal — 60 days out': {
    name: 'Renovación — 60 días antes',
    description: 'Activa esta plantilla 60 días ANTES de la fecha de renovación. Pasa la fecha de renovación como triggerDate.',
    tasks: {
      'Notify client of upcoming renewal':  'Notificar al cliente sobre la renovación próxima',
      'Review current coverage adequacy':   'Revisar si la cobertura actual sigue siendo adecuada',
      'Present renewal options':            'Presentar opciones de renovación',
      'Get signature on renewal docs':      'Obtener firma en documentos de renovación',
      'Confirm renewal processed':          'Confirmar que la renovación se procesó',
    },
    taskDescriptions: {
      'Notify client of upcoming renewal':  'Enviar email + mensaje 60 días antes con la fecha de renovación y el resumen de cobertura actual.',
      'Review current coverage adequacy':   'Revisar historial de reclamos, cambios de vida, ver si la cobertura aún calza.',
      'Present renewal options':            'Construir una Cotización con la renovación + 1-2 alternativas si hay mejores opciones.',
      'Get signature on renewal docs':      'Enviar solicitud de eFirma para la aplicación de renovación o el formulario de cambio de cobertura.',
      'Confirm renewal processed':          'Verificar con la aseguradora que la renovación pasó, nuevas tarjetas de ID si aplica.',
    },
  },
  'Medicare AEP — Annual Review': {
    name: 'Medicare AEP — Revisión Anual',
    description: 'Período de Inscripción Anual (15 oct – 7 dic). Acompaña a tus clientes Medicare en los cambios de plan año tras año.',
    tasks: {
      'AEP outreach — schedule review call':   'Contacto AEP — agendar llamada de revisión',
      'Pull current plan ANOC notice':         'Obtener Aviso Anual de Cambios (ANOC) del plan actual',
      'Run plan comparison':                   'Hacer comparación de planes',
      'Conduct review call':                   'Realizar la llamada de revisión',
      'Process plan change (if elected)':      'Procesar cambio de plan (si lo eligen)',
      'Confirm new plan effective Jan 1':      'Confirmar plan nuevo efectivo el 1 de enero',
    },
    taskDescriptions: {
      'AEP outreach — schedule review call':   'Enviar email de introducción AEP + agendar llamada de 30 min.',
      'Pull current plan ANOC notice':         'Obtener el Annual Notice of Change de la aseguradora para comparar costos/formulario.',
      'Run plan comparison':                   'Usar Medicare Plan Finder + portales de aseguradoras para comparar el plan actual vs alternativas.',
      'Conduct review call':                   'Recorrer hallazgos, recomendar quedarse o cambiar.',
      'Process plan change (if elected)':      'Enviar Scope of Appointment + aplicación de inscripción.',
      'Confirm new plan effective Jan 1':      'Verificar confirmación de inscripción, entrega de nuevas tarjetas de ID, formulario revisado.',
    },
  },
  'Claim Assistance': {
    name: 'Asistencia de Reclamo',
    description: 'Acompaña al cliente en la presentación y seguimiento de un reclamo.',
    tasks: {
      'Gather incident details':            'Reunir detalles del incidente',
      'File claim with carrier':            'Presentar el reclamo a la aseguradora',
      'Send confirmation to client':        'Enviar confirmación al cliente',
      'Follow up with adjuster (week 1)':   'Seguir con el ajustador (semana 1)',
      'Verify settlement / payment':        'Verificar liquidación / pago',
      'Close claim — client check-in':      'Cerrar reclamo — chequeo con el cliente',
    },
    taskDescriptions: {
      'Gather incident details':            'Fecha, lugar, partes involucradas, fotos, reportes de policía/médicos.',
      'File claim with carrier':            'Enviar el FNOL por el portal de la aseguradora o línea de reclamos. Capturar número de reclamo.',
      'Send confirmation to client':        'Enviar email con número de reclamo, contacto del ajustador, plazo esperado.',
      'Follow up with adjuster (week 1)':   'Llamar al ajustador para actualización, presionar para agendar inspección.',
      'Verify settlement / payment':        'Confirmar monto del pago, comparar vs deducible, disputar si es necesario.',
      'Close claim — client check-in':      'Confirmar satisfacción, atender preocupaciones restantes, registrar lecciones aprendidas.',
    },
  },
  'Cross-sell — Auto + Home Bundle': {
    name: 'Cross-sell — Paquete Auto + Hogar',
    description: 'Cliente existente con una sola línea. Vender un descuento por paquete multi-línea.',
    tasks: {
      'Pull current carrier rates':         'Obtener tarifas actuales de la aseguradora',
      'Identify potential savings':         'Identificar ahorros potenciales',
      'Send bundle proposal':               'Enviar propuesta del paquete',
      'Follow-up call (day 5)':             'Llamada de seguimiento (día 5)',
      'Final follow-up (day 14)':           'Seguimiento final (día 14)',
    },
    taskDescriptions: {
      'Pull current carrier rates':         'Cotización rápida en la línea que falta (auto si tienen hogar, o viceversa).',
      'Identify potential savings':         'Calcular descuento de paquete + ahorro anual total vs gasto actual.',
      'Send bundle proposal':               'Enviar email con comparación: lo que pagan ahora vs el paquete, con % de descuento.',
      'Follow-up call (day 5)':             'Atender dudas, manejar objeciones, cierre suave.',
      'Final follow-up (day 14)':           'Último contacto antes de cerrar la oportunidad. Cierre-ganado o cierre-perdido.',
    },
  },
};

/**
 * Returns a localized COPY of the template if it's a known system template
 * AND the lang is 'es'. Otherwise returns the template unchanged.
 */
function localizeTemplate(tpl, lang) {
  if (!tpl || lang !== 'es' || !tpl.isSystem) return tpl;
  const tr = SYSTEM_TEMPLATE_ES[tpl.name];
  if (!tr) return tpl;
  return {
    ...tpl,
    name: tr.name,
    description: tr.description,
    tasks: (tpl.tasks || []).map((t) => ({
      ...t,
      title: tr.tasks[t.title] || t.title,
      description: tr.taskDescriptions[t.title] || t.description,
    })),
  };
}

// ----------------------------------------------------------------------
// Main view
// ----------------------------------------------------------------------

export function ActionTemplatesView({ currentUser, lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [view, setView] = useState("list"); // list | editor | detail
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filterCategory, setFilterCategory] = useState("ALL");

  const refresh = useCallback(async () => {
    setLoading(true); setError("");
    try { setTemplates(await listTemplates()); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const h = (e) => {
      if (e?.detail?.view === "templates") {
        setView("list"); setEditing(null); setOpenId(null);
      }
    };
    window.addEventListener("deskte:nav-reset", h);
    return () => window.removeEventListener("deskte:nav-reset", h);
  }, []);

  const filtered = useMemo(() => {
    const base = filterCategory === "ALL" ? templates : templates.filter((tpl) => tpl.category === filterCategory);
    return base.map((tpl) => localizeTemplate(tpl, lang));
  }, [templates, filterCategory, lang]);

  if (view === "editor") {
    return (
      <TemplateEditor
        initial={editing}
        lang={lang}
        onCancel={() => { setView("list"); setEditing(null); }}
        onSaved={async () => { await refresh(); setView("list"); setEditing(null); }}
      />
    );
  }
  if (view === "detail" && openId) {
    return (
      <TemplateDetail
        templateId={openId}
        lang={lang}
        onBack={() => { setView("list"); setOpenId(null); }}
        onEdit={(tpl) => { setEditing(tpl); setView("editor"); }}
        onClone={(tpl) => { setEditing({ ...tpl, id: undefined, isSystem: false, name: `${tpl.name} (copia)` }); setView("editor"); }}
        onDeleted={async () => { await refresh(); setView("list"); setOpenId(null); }}
      />
    );
  }

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: ".75rem" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.4rem", color: "var(--c-white,#eef6ff)" }}>
            ⚡ {t("Plantillas de proceso", "Action Templates")}
          </h2>
          <div style={{ fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)", marginTop: 2 }}>
            {t("Procesos pre-armados para aplicar a clientes en un click. Crea las tareas con sus fechas calculadas automáticamente.",
               "Pre-baked processes you apply to a client in one click. Tasks are auto-created with computed due dates.")}
          </div>
        </div>
        <button onClick={() => { setEditing(null); setView("editor"); }} style={primaryBtn()}>
          <Plus size={16} /> {t("Nueva plantilla", "New template")}
        </button>
      </div>

      {/* Category filters */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: "1rem" }}>
        <CategoryChip active={filterCategory === "ALL"} onClick={() => setFilterCategory("ALL")} label={t("Todas", "All")} count={templates.length} />
        {Object.entries(CATEGORY_META).map(([key, meta]) => {
          const count = templates.filter((tpl) => tpl.category === key).length;
          if (count === 0) return null;
          return <CategoryChip key={key} active={filterCategory === key} onClick={() => setFilterCategory(key)} label={meta.label} count={count} color={meta.color} />;
        })}
      </div>

      {error && <ErrorBanner text={error} onClose={() => setError("")} />}

      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--c-text2,#7aa3c0)" }}>
          <Loader2 size={20} className="dkt-spin" /> {t("Cargando…", "Loading…")}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "3rem 1rem",
          background: "var(--c-surface,#0d1c2e)", border: "1px dashed var(--c-border,#1a3550)",
          borderRadius: 16, color: "var(--c-text2,#7aa3c0)",
        }}>
          {t("No hay plantillas en esta categoría.", "No templates in this category.")}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: "1rem" }}>
          {filtered.map((tpl) => (
            <TemplateCard
              key={tpl.id}
              tpl={tpl}
              lang={lang}
              onOpen={() => { setOpenId(tpl.id); setView("detail"); }}
              onEdit={() => { setEditing(tpl); setView("editor"); }}
              onClone={() => { setEditing({ ...tpl, id: undefined, isSystem: false, name: `${tpl.name} (${t("copia", "copy")})` }); setView("editor"); }}
              onDelete={async () => {
                if (!(await confirmDialog({ message: t(`¿Eliminar "${tpl.name}"?`, `Delete "${tpl.name}"?`), danger: true }))) return;
                try { await deleteTemplate(tpl.id); await refresh(); } catch (err) { setError(err.message); }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryChip({ active, onClick, label, count, color = "#00d4ff" }) {
  return (
    <button onClick={onClick} style={{
      padding: ".4rem .85rem", borderRadius: 999,
      background: active ? color + "22" : "var(--c-surface2,#112538)",
      color: active ? color : "var(--c-text2,#7aa3c0)",
      border: `1px solid ${active ? color + "66" : "var(--c-border,#1a3550)"}`,
      fontSize: ".78rem", fontWeight: 600, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
    }}>
      {label} <span style={{ opacity: .7 }}>({count})</span>
    </button>
  );
}

function TemplateCard({ tpl, lang, onOpen, onEdit, onClone, onDelete }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const meta = CATEGORY_META[tpl.category] || CATEGORY_META.GENERAL;
  const taskCount = (tpl.tasks || []).length;
  return (
    <div onClick={onOpen} style={{
      background: "var(--c-surface,#0d1c2e)",
      border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 14, padding: "1.1rem",
      display: "flex", flexDirection: "column", gap: ".7rem",
      cursor: "pointer", transition: "border-color .15s",
    }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = meta.color + "66"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--c-border,#1a3550)"; }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 11,
          background: meta.color + "22", border: `1px solid ${meta.color}55`,
          display: "grid", placeItems: "center", fontSize: "1.4rem",
          flexShrink: 0,
        }}>
          {tpl.icon || "📋"}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--c-white,#eef6ff)", marginBottom: 3 }}>
            {tpl.name}
          </div>
          <div style={{ fontSize: ".72rem", display: "inline-flex", alignItems: "center", gap: 8, color: meta.color, fontWeight: 600 }}>
            <span style={{ background: meta.color + "22", padding: "2px 8px", borderRadius: 999, fontSize: ".68rem", fontWeight: 700, letterSpacing: ".04em" }}>
              {meta.label}
            </span>
            {tpl.isSystem && (
              <span title={t("Plantilla del sistema", "System template")} style={{ color: "#a855f7" }}>
                ⭐ {t("Sistema", "System")}
              </span>
            )}
          </div>
        </div>
      </div>

      {tpl.description && (
        <div style={{ fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {tpl.description}
        </div>
      )}

      <div style={{ display: "flex", gap: ".75rem", fontSize: ".78rem", color: "var(--c-text2,#7aa3c0)" }}>
        <span><Clock size={11} style={{ verticalAlign: "-1px" }} /> {taskCount} {t("tareas", "tasks")}</span>
        <span>· {tpl.applyCount || 0} {t("aplicadas", "applied")}</span>
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        {!tpl.isSystem && (
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }} style={btn("primary")} title={t("Editar", "Edit")}>
            <Edit2 size={12} /> {t("Editar", "Edit")}
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); onClone(); }} style={btn("ghost")} title={t("Clonar como custom", "Clone as custom")}>
          <Copy size={12} /> {t("Clonar", "Clone")}
        </button>
        {!tpl.isSystem && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} style={btn("danger")} title={t("Eliminar", "Delete")}>
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// TemplateEditor
// ----------------------------------------------------------------------

function TemplateEditor({ initial, onCancel, onSaved, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [category, setCategory] = useState(initial?.category || "GENERAL");
  const [icon, setIcon] = useState(initial?.icon || "📋");
  const [policyTypes, setPolicyTypes] = useState(initial?.policyTypes || []);
  const [tasks, setTasks] = useState(() => {
    const init = Array.isArray(initial?.tasks) ? initial.tasks : null;
    return init && init.length > 0
      ? init.map((tk) => ({ ...tk, _localId: Math.random().toString(36).slice(2) }))
      : [{ _localId: "x1", title: "First task", daysOffset: 0, priority: "MEDIUM", description: "", category: "follow-up", assigneeRule: "owner" }];
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function addTask() {
    setTasks((arr) => [...arr, {
      _localId: Math.random().toString(36).slice(2),
      title: "New task", daysOffset: arr.length, priority: "MEDIUM",
      description: "", category: "follow-up", assigneeRule: "owner",
    }]);
  }
  function updateTask(localId, patch) {
    setTasks((arr) => arr.map((t) => t._localId === localId ? { ...t, ...patch } : t));
  }
  function removeTask(localId) {
    setTasks((arr) => arr.filter((t) => t._localId !== localId));
  }
  function moveTask(localId, dir) {
    setTasks((arr) => {
      const i = arr.findIndex((t) => t._localId === localId);
      if (i < 0) return arr;
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const out = arr.slice();
      [out[i], out[j]] = [out[j], out[i]];
      return out;
    });
  }

  async function save() {
    if (!name.trim()) { setError(t("El nombre es obligatorio.", "Name is required.")); return; }
    if (tasks.length === 0) { setError(t("Agrega al menos una tarea.", "Add at least one task.")); return; }
    setBusy(true); setError("");
    try {
      const payload = {
        name, description: description || null, category, icon, policyTypes,
        tasks: tasks.map(({ _localId, ...t }) => t),
      };
      if (initial?.id) await updateTemplate(initial.id, payload);
      else await createTemplate(payload);
      await onSaved();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div>
          <button onClick={onCancel} style={{ background: "none", border: "none", color: "var(--c-text2,#7aa3c0)", cursor: "pointer", fontSize: ".82rem", marginBottom: 6 }}>
            ← {t("Volver", "Back")}
          </button>
          <h2 style={{ margin: 0, fontSize: "1.4rem", color: "var(--c-white,#eef6ff)" }}>
            {initial?.id ? t("Editar plantilla", "Edit template") : t("Nueva plantilla", "New template")}
          </h2>
        </div>
        <button onClick={save} disabled={busy} style={primaryBtn()}>
          {busy ? <Loader2 size={14} className="dkt-spin" /> : <Check size={14} />}
          {busy ? t("Guardando…", "Saving…") : (initial?.id ? t("Guardar cambios", "Save changes") : t("Crear plantilla", "Create template"))}
        </button>
      </div>

      {error && <ErrorBanner text={error} onClose={() => setError("")} />}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: "1rem", alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Card title={t("Información", "Information")}>
            <Field label={t("Nombre", "Name")} required>
              <input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder={t("ej. Onboarding cliente Vida", "e.g. Onboarding Life Client")}
                     style={inputStyle()} />
            </Field>
            <Field label={t("Descripción", "Description")}>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                        rows={2} style={{ ...inputStyle(), resize: "vertical" }} />
            </Field>
          </Card>

          <Card title={t(`Tareas (${tasks.length})`, `Tasks (${tasks.length})`)}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {tasks.map((tk, i) => (
                <TaskRow key={tk._localId} tk={tk} idx={i} total={tasks.length}
                         onUpdate={(p) => updateTask(tk._localId, p)}
                         onRemove={() => removeTask(tk._localId)}
                         onMove={(d) => moveTask(tk._localId, d)}
                         lang={lang} />
              ))}
            </div>
            <button onClick={addTask} style={{
              marginTop: ".75rem", padding: ".55rem .9rem", borderRadius: 8,
              background: "#00d4ff22", color: "#00d4ff", border: "1px solid #00d4ff66",
              cursor: "pointer", fontWeight: 600, fontSize: ".82rem",
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <Plus size={13} /> {t("Agregar tarea", "Add task")}
            </button>
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Card title={t("Apariencia", "Display")}>
            <Field label={t("Icono", "Icon")}>
              <input value={icon} onChange={(e) => setIcon(e.target.value.slice(0, 4))}
                     placeholder="📋" style={{ ...inputStyle(), fontSize: "1.4rem", textAlign: "center" }} />
              <div style={{ fontSize: ".72rem", color: "var(--c-text3,#45688a)", marginTop: 4 }}>
                {t("1 emoji.", "1 emoji.")}
              </div>
            </Field>
            <Field label={t("Categoría", "Category")}>
              <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle()}>
                {Object.entries(CATEGORY_META).map(([k, m]) => (<option key={k} value={k}>{m.label}</option>))}
              </select>
            </Field>
            <Field label={t("Tipos de póliza relevantes", "Relevant policy types")}>
              <PolicyTypePicker value={policyTypes} onChange={setPolicyTypes} lang={lang} />
              <div style={{ fontSize: ".72rem", color: "var(--c-text3,#45688a)", marginTop: 4 }}>
                {t("Si dejas vacío, aplica a todos.", "Empty = applies to any.")}
              </div>
            </Field>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PolicyTypePicker({ value, onChange, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const ALL = ["HEALTH", "LIFE", "AUTO", "HOME", "MEDICARE_SUPPLEMENT", "MEDICARE_ADVANTAGE", "DENTAL", "VISION", "DISABILITY", "ANNUITY", "COMMERCIAL"];
  function toggle(p) {
    if (value.includes(p)) onChange(value.filter((x) => x !== p));
    else onChange([...value, p]);
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {ALL.map((p) => {
        const on = value.includes(p);
        return (
          <button key={p} onClick={() => toggle(p)} style={{
            padding: "3px 9px", borderRadius: 999, fontSize: ".7rem", cursor: "pointer",
            background: on ? "#00d4ff22" : "var(--c-surface2,#112538)",
            color: on ? "#00d4ff" : "var(--c-text2,#7aa3c0)",
            border: `1px solid ${on ? "#00d4ff66" : "var(--c-border,#1a3550)"}`,
            fontWeight: 600,
          }}>{p}</button>
        );
      })}
    </div>
  );
}

function TaskRow({ tk, idx, total, onUpdate, onRemove, onMove, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 10, padding: ".7rem .85rem",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <button onClick={() => onMove(-1)} disabled={idx === 0} style={miniBtn()}>↑</button>
          <button onClick={() => onMove(1)} disabled={idx === total - 1} style={miniBtn()}>↓</button>
        </div>
        <input value={tk.title} onChange={(e) => onUpdate({ title: e.target.value })}
               placeholder={t("Título de la tarea", "Task title")}
               style={{ ...inputStyle(), flex: 1, padding: ".4rem .6rem", fontSize: ".82rem" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 4, color: "var(--c-text2,#7aa3c0)", fontSize: ".75rem" }}>
          <span>{t("día", "day")}</span>
          <input type="number" value={tk.daysOffset} onChange={(e) => onUpdate({ daysOffset: parseInt(e.target.value, 10) || 0 })}
                 style={{ ...inputStyle(), width: 70, padding: ".3rem .5rem", textAlign: "center", fontSize: ".82rem" }} />
        </div>
        <select value={tk.priority || "MEDIUM"} onChange={(e) => onUpdate({ priority: e.target.value })}
                style={{ ...inputStyle(), width: 110, padding: ".4rem .5rem", fontSize: ".78rem" }}>
          {Object.entries(PRIORITY_META).map(([k, m]) => (<option key={k} value={k}>{m.label}</option>))}
        </select>
        <button onClick={() => setOpen((o) => !o)} style={{ ...miniBtn(), padding: 4 }}>
          <ChevronDown size={14} style={{ transform: open ? "rotate(180deg)" : "none" }} />
        </button>
        <button onClick={onRemove} style={{ ...miniBtn(), color: "#fca5a5" }}>
          <Trash2 size={13} />
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--c-border,#1a3550)", display: "flex", flexDirection: "column", gap: 8 }}>
          <Field label={t("Descripción", "Description")}>
            <textarea value={tk.description || ""} onChange={(e) => onUpdate({ description: e.target.value })}
                      rows={2} style={{ ...inputStyle(), resize: "vertical" }} />
          </Field>
          <Field label={t("Categoría (etiqueta)", "Category (tag)")}>
            <input value={tk.category || ""} onChange={(e) => onUpdate({ category: e.target.value })}
                   placeholder="follow-up" style={inputStyle()} />
          </Field>
          <Field label={t("Asignar a", "Assign to")}>
            <select value={tk.assigneeRule || "owner"} onChange={(e) => onUpdate({ assigneeRule: e.target.value })} style={inputStyle()}>
              <option value="owner">{t("Quien aplica la plantilla", "Whoever applies the template")}</option>
              <option value="client_agent">{t("Agente del cliente", "Client's agent of record")}</option>
              <option value="unassigned">{t("Sin asignar", "Unassigned")}</option>
            </select>
          </Field>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// TemplateDetail
// ----------------------------------------------------------------------

function TemplateDetail({ templateId, onBack, onEdit, onClone, onDeleted, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [tpl, setTpl] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    getTemplate(templateId)
      .then((data) => { if (!cancel) setTpl(data); })
      .catch((err) => { if (!cancel) setError(err.message); });
    return () => { cancel = true; };
  }, [templateId]);

  if (error) return <div style={{ padding: "1.5rem", color: "#fca5a5" }}>{error}</div>;
  if (!tpl) return <div style={{ padding: "1.5rem", color: "var(--c-text2,#7aa3c0)" }}>{t("Cargando…", "Loading…")}</div>;

  // Localize for display only — clone/edit handlers (which receive `tpl`)
  // get the raw original via the inline arrow below, since custom edits
  // should be made on the underlying English content not the localized one.
  const localTpl = localizeTemplate(tpl, lang);
  const meta = CATEGORY_META[tpl.category] || CATEGORY_META.GENERAL;

  return (
    <div style={{ padding: "1.5rem", maxWidth: 900, margin: "0 auto" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--c-text2,#7aa3c0)", cursor: "pointer", fontSize: ".82rem", marginBottom: 8 }}>
        ← {t("Volver", "Back")}
      </button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: "1.25rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <div style={{
            width: 60, height: 60, borderRadius: 14,
            background: meta.color + "22", border: `1px solid ${meta.color}55`,
            display: "grid", placeItems: "center", fontSize: "2rem",
          }}>{localTpl.icon || "📋"}</div>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.35rem", color: "var(--c-white,#eef6ff)" }}>{localTpl.name}</h2>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
              <span style={{ fontSize: ".7rem", color: meta.color, background: meta.color + "22", padding: "3px 9px", borderRadius: 999, fontWeight: 700, letterSpacing: ".04em" }}>
                {meta.label}
              </span>
              {localTpl.isSystem && <span style={{ fontSize: ".7rem", color: "#a855f7" }}>⭐ {t("Plantilla del sistema", "System template")}</span>}
              <span style={{ fontSize: ".7rem", color: "var(--c-text3,#45688a)" }}>· {(localTpl.tasks || []).length} {t("tareas", "tasks")}</span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {!localTpl.isSystem && (
            <button onClick={() => onEdit(tpl)} style={btn("primary")}><Edit2 size={12} /> {t("Editar", "Edit")}</button>
          )}
          <button onClick={() => onClone(tpl)} style={btn("ghost")}><Copy size={12} /> {t("Clonar", "Clone")}</button>
        </div>
      </div>

      {localTpl.description && (
        <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem", color: "var(--c-text2,#7aa3c0)", fontSize: ".88rem", lineHeight: 1.55, marginBottom: "1rem" }}>
          {localTpl.description}
        </div>
      )}

      <Card title={t("Línea de tiempo", "Timeline")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(localTpl.tasks || []).map((tk, i) => (
            <TaskTimelineRow key={i} idx={i} tk={tk} lang={lang} />
          ))}
        </div>
      </Card>
    </div>
  );
}

function TaskTimelineRow({ idx, tk, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const pri = PRIORITY_META[tk.priority] || PRIORITY_META.MEDIUM;
  const offsetLabel = tk.daysOffset === 0
    ? t("Inmediato", "Day 0")
    : tk.daysOffset > 0
      ? `+${tk.daysOffset}d`
      : `${tk.daysOffset}d`;
  return (
    <div style={{
      display: "flex", gap: 12, alignItems: "flex-start",
      background: "var(--c-surface2,#112538)",
      border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 10, padding: ".75rem .9rem",
    }}>
      <div style={{
        width: 56, flexShrink: 0, fontSize: ".82rem", fontWeight: 700,
        color: tk.daysOffset < 0 ? "#a855f7" : tk.daysOffset === 0 ? "#10b981" : "#3b82f6",
        textAlign: "center", padding: "4px 0", borderRadius: 6,
        background: tk.daysOffset === 0 ? "#10b98115" : "#3b82f615",
      }}>{offsetLabel}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: ".88rem", fontWeight: 700, color: "var(--c-white,#eef6ff)", marginBottom: 2 }}>
          {idx + 1}. {tk.title}
        </div>
        {tk.description && (
          <div style={{ fontSize: ".78rem", color: "var(--c-text2,#7aa3c0)", lineHeight: 1.5, marginBottom: 4 }}>
            {tk.description}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, fontSize: ".7rem", color: "var(--c-text3,#45688a)" }}>
          <span style={{ color: pri.color, fontWeight: 700 }}>{pri.label}</span>
          {tk.category && <span>· {tk.category}</span>}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// ApplyTemplateModal — used from ClientDetail
// ----------------------------------------------------------------------

export function ApplyTemplateModal({ client, onClose, onApplied, lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  // LOCAL today as 'YYYY-MM-DD' — toISOString() gives the UTC date, which rolls
  // to tomorrow in Florida evenings (feedback_local_day_not_utc).
  const [triggerDate, setTriggerDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listTemplates()
      .then((data) => setTemplates(data.map((tpl) => localizeTemplate(tpl, lang))))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [lang]);

  async function doApply() {
    if (!selected) return;
    setBusy(true); setError("");
    try {
      const result = await applyTemplate(selected.id, {
        clientId: client.id,
        triggerDate: triggerDate ? new Date(triggerDate).toISOString() : undefined,
      });
      onApplied(result);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 100,
      background: "rgba(0,0,0,.7)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border2,#1f3f60)",
        borderRadius: 16, maxWidth: 720, width: "100%", maxHeight: "85vh",
        display: "flex", flexDirection: "column", boxShadow: "0 24px 80px rgba(0,0,0,.6)",
      }}>
        <div style={{ padding: "1.25rem", borderBottom: "1px solid var(--c-border,#1a3550)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Sparkles size={18} color="#00d4ff" />
              <h3 style={{ margin: 0, fontSize: "1.1rem", color: "var(--c-white,#eef6ff)" }}>
                {selected ? t("Aplicar plantilla", "Apply template") : t("Elige una plantilla", "Choose a template")}
              </h3>
            </div>
            <div style={{ fontSize: ".78rem", color: "var(--c-text2,#7aa3c0)", marginTop: 2 }}>
              {t("para", "for")} <strong>{client.firstName} {client.lastName}</strong>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--c-text2,#7aa3c0)", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.25rem" }}>
          {error && <ErrorBanner text={error} onClose={() => setError("")} />}
          {loading ? (
            <div style={{ textAlign: "center", padding: "2rem", color: "var(--c-text2,#7aa3c0)" }}>
              <Loader2 size={20} className="dkt-spin" /> {t("Cargando…", "Loading…")}
            </div>
          ) : !selected ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem" }}>
              {templates.map((tpl) => (
                <button key={tpl.id} onClick={() => setSelected(tpl)} style={{
                  textAlign: "left", padding: ".85rem 1rem", borderRadius: 10,
                  background: "var(--c-surface2,#112538)", color: "var(--c-white,#eef6ff)",
                  border: "1px solid var(--c-border,#1a3550)", cursor: "pointer",
                  display: "flex", gap: 10, alignItems: "flex-start",
                }}>
                  <div style={{ fontSize: "1.6rem", flexShrink: 0 }}>{tpl.icon || "📋"}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: ".88rem", marginBottom: 2 }}>{tpl.name}</div>
                    <div style={{ fontSize: ".72rem", color: "var(--c-text2,#7aa3c0)" }}>
                      {(tpl.tasks || []).length} {t("tareas", "tasks")} · {CATEGORY_META[tpl.category]?.label || tpl.category}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: "1rem" }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 11, background: "#00d4ff22",
                  border: "1px solid #00d4ff55", display: "grid", placeItems: "center", fontSize: "1.6rem",
                }}>{selected.icon || "📋"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--c-white,#eef6ff)" }}>{selected.name}</div>
                  {selected.description && <div style={{ fontSize: ".78rem", color: "var(--c-text2,#7aa3c0)", marginTop: 2 }}>{selected.description}</div>}
                </div>
                <button onClick={() => setSelected(null)} style={btn("ghost")}>
                  ← {t("Cambiar", "Change")}
                </button>
              </div>

              <div style={{
                background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)",
                borderRadius: 10, padding: ".75rem 1rem", marginBottom: "1rem",
              }}>
                <Field label={t("Fecha base (las fechas se calculan desde aquí)", "Base date (task due dates are computed from here)")}>
                  <DateField value={triggerDate} onChange={(e) => setTriggerDate(e.target.value)} style={inputStyle()} />
                </Field>
              </div>

              <div style={{ fontSize: ".75rem", color: "var(--c-text3,#45688a)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, marginBottom: 8 }}>
                {t(`Se crearán ${selected.tasks?.length || 0} tareas`, `Will create ${selected.tasks?.length || 0} tasks`)}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflow: "auto" }}>
                {(selected.tasks || []).map((tk, i) => (
                  <PreviewTaskRow key={i} idx={i} tk={tk} triggerDate={triggerDate} lang={lang} />
                ))}
              </div>
            </div>
          )}
        </div>

        {selected && (
          <div style={{ padding: "1rem 1.25rem", borderTop: "1px solid var(--c-border,#1a3550)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={btn("ghost")}>{t("Cancelar", "Cancel")}</button>
            <button onClick={doApply} disabled={busy} style={primaryBtn()}>
              {busy ? <Loader2 size={14} className="dkt-spin" /> : <Check size={14} />}
              {busy ? t("Aplicando…", "Applying…") : t("Aplicar plantilla", "Apply template")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewTaskRow({ idx, tk, triggerDate, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const due = new Date(triggerDate);
  due.setDate(due.getDate() + (Number(tk.daysOffset) || 0));
  const pri = PRIORITY_META[tk.priority] || PRIORITY_META.MEDIUM;
  return (
    <div style={{
      display: "flex", gap: 10, alignItems: "center",
      background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 8, padding: ".5rem .75rem",
    }}>
      <div style={{
        width: 28, fontSize: ".7rem", color: "var(--c-text3,#45688a)", textAlign: "center", fontWeight: 700,
      }}>{idx + 1}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: ".82rem", fontWeight: 600, color: "var(--c-white,#eef6ff)" }}>{tk.title}</div>
        <div style={{ fontSize: ".68rem", color: "var(--c-text3,#45688a)" }}>
          <Calendar size={9} style={{ verticalAlign: "-1px" }} /> {due.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
          · <span style={{ color: pri.color, fontWeight: 700 }}>{pri.label}</span>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

function Card({ title, children }) {
  return (
    <div style={{
      background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 12, padding: "1rem 1.1rem",
    }}>
      <div style={{
        fontSize: ".72rem", letterSpacing: ".06em", textTransform: "uppercase",
        color: "var(--c-text3,#45688a)", fontWeight: 700, marginBottom: ".75rem",
      }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)", marginBottom: 4, fontWeight: 600 }}>
        {label}{required ? <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span> : null}
      </div>
      {children}
    </label>
  );
}

function inputStyle() {
  return {
    width: "100%", padding: ".55rem .75rem", borderRadius: 8,
    background: "var(--c-surface2,#112538)", color: "var(--c-white,#eef6ff)",
    border: "1px solid var(--c-border,#1a3550)", fontSize: ".88rem", outline: "none",
  };
}

function ErrorBanner({ text, onClose }) {
  return (
    <div style={{
      background: "#3a1a1a", border: "1px solid #ef4444", borderRadius: 10,
      padding: ".75rem 1rem", marginBottom: 12, color: "#fca5a5",
      display: "flex", alignItems: "center", gap: 10, fontSize: ".88rem",
    }}>
      <AlertCircle size={16} /><span style={{ flex: 1 }}>{text}</span>
      {onClose && <button onClick={onClose} style={{ background: "none", border: "none", color: "#fca5a5", cursor: "pointer" }}>×</button>}
    </div>
  );
}

function primaryBtn() {
  return {
    padding: ".55rem 1.25rem", borderRadius: 9,
    background: "linear-gradient(135deg, #00d4ff, #00a8c8)", color: "#0a1628",
    border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".82rem",
    display: "inline-flex", alignItems: "center", gap: 6,
  };
}

function btn(kind) {
  const map = {
    primary: { bg: "#00d4ff22", color: "#00d4ff", border: "#00d4ff66" },
    ghost:   { bg: "var(--c-surface2,#112538)", color: "var(--c-text2,#7aa3c0)", border: "var(--c-border,#1a3550)" },
    danger:  { bg: "#7f1d1d22", color: "#fca5a5", border: "#ef444466" },
  }[kind];
  return {
    background: map.bg, color: map.color, border: `1px solid ${map.border}`,
    padding: ".4rem .65rem", borderRadius: 8, cursor: "pointer", fontSize: ".75rem",
    display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600,
  };
}

function miniBtn() {
  return {
    background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)",
    color: "var(--c-text2,#7aa3c0)", padding: 3, borderRadius: 5, cursor: "pointer",
    fontSize: 11, lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center",
  };
}
